import React from 'react';
import useFetch from 'use-http';
import Moment from 'react-moment';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heading } from '@/components/ui/shared/heading';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SlashIcon, Download } from 'lucide-react';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { useUserState } from '@/components/context/UserContext';

const CouponDetails = () => {
  const history = useHistory();
  const { get: getFile } = useFetch('/UserAccount/DownloadAmisFile');
  const data = history.location.state?.data;
  const userState = useUserState();

  const handleFileDownload = async (filename, fileType, fileGuid) => {
    await getFile(
      `?fileType=${fileType}&fileGuid=${fileGuid}&category=InstrumentalResearch&espbiEsi=${userState.espbiEsi}`,
    ).then((res) => {
      const base64Data = res;
      const blob = base64toBlob(base64Data, 'application/pdf');
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = `${filename}.pdf`;
      document.body.appendChild(downloadLink);
      setTimeout(() => {
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }, 120);
    });
  };

  const base64toBlob = (base64Data, contentType = '') => {
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);
      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  };

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <Breadcrumb className="px-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/userAccount/coupons">Kuponai</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <SlashIcon />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>{data.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Card 1: Coupon Info */}
      <Card>
        <CardHeader>
          <Heading variant="userAccountDetailsTitle">
            Kupono informacija
          </Heading>
        </CardHeader>
        <CardContent className="space-y-2">
          <div>
            <span>Pavadinimas: </span>
            <span>{data.name}</span>
          </div>

          {data.value && (
            <div>
              <span>Vertė: </span>
              <span>{data.value}€</span>
            </div>
          )}

          <div>
            <span>Numeris: </span>
            <span>{data.number}</span>
          </div>

          <div>
            <span>Balansas: </span>
            <span>{Number(data.balance) || 0}€</span>
          </div>

          {data.validTo && (
            <div>
              <span>Galioja iki: </span>
              <span>
                {new Date(Date.parse(data.validTo)).getUTCHours() === 0 ? (
                  <Moment utc format="YYYY-MM-DD">
                    {data.validTo}
                  </Moment>
                ) : (
                  <Moment utc format="YYYY-MM-DD HH:mm">
                    {data.validTo}
                  </Moment>
                )}
              </span>
            </div>
          )}

          {data.company && (
            <div>
              <span>Galioja: </span>
              <span>{data.company}</span>
            </div>
          )}

          <div>
            <span>Būsena: </span>
            <span>{data.statusId}</span>
          </div>
        </CardContent>
        <CardFooter />
      </Card>

      {/* Card 2: Download */}
      {data.file?.guid && (
        <Card>
          <CardHeader>
            <Heading variant="userAccountDetailsTitle">Dokumentai</Heading>
          </CardHeader>
          <CardContent>
            <Button
              variant="light"
              onClick={() => {
                const fileName = `Dovanu kuponas - ${data.number}`;
                handleFileDownload(fileName, data.file.type, data.file.guid);
              }}
            >
              <Download />
              Atsisiųsti PDF
            </Button>
          </CardContent>
          <CardFooter />
        </Card>
      )}

      {/* Card 3: Usage History */}
      {data.usings?.length > 0 && (
        <Card>
          <CardHeader>
            <Heading variant="userAccountDetailsTitle">
              Kupono panaudojimo istorija
            </Heading>
          </CardHeader>
          <CardContent className="space-y-4">
            {data.usings.map((entry, index) => (
              <div key={index} className="ml-4 space-y-1">
                {entry.dateOfUse && (
                  <div>
                    <span>Data: </span>
                    <span>
                      <Moment utc format="YYYY-MM-DD HH:mm">
                        {entry.dateOfUse}
                      </Moment>
                    </span>
                  </div>
                )}
                <div>
                  <span>Panaudota suma: </span>
                  <span>{entry.usedSum}€</span>
                </div>
              </div>
            ))}
          </CardContent>
          <CardFooter />
        </Card>
      )}
    </div>
  );
};

export default CouponDetails;
